/**
 * RGM Studio — landing (Studio + entry, My Studio Projects).
 *
 * Studio + opens a quick sheet (name, Start with a beat / Start blank).
 * Sessions are grouped by what they contain: All · Beat Sessions ·
 * Vocal Only. Drafts are never hidden — every saved session is in All.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import api from "@/lib/api";
import { studioSupport } from "../lib/studioEngine";
import SharedNavigation from "../components/SharedNavigation";
import { StudioCreateSheet } from "../components/studio/StudioCreateSheet";
import { StudioSessionCard } from "../components/studio/StudioSessionCard";
import { StudioProBanner } from "../components/studio/StudioProBanner";
import { CollabEntry } from "../components/studio/CollabEntry";

export const SESSION_TABS = [
  { key: "all", label: "All", match: () => true },
  { key: "beat", label: "Beat Sessions", match: (p) => !!p.beat_source },
  { key: "vocal", label: "No Beat Yet", match: (p) => !p.beat_source },
  { key: "shared", label: "Shared", match: (p) => p.access_role === "collaborator" || (p.collaborator_count || 0) > 0 },
];

export default function StudioPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [studioPro, setStudioPro] = useState(null);
  const support = useMemo(() => studioSupport(), []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/studio/projects");
        setProjects(r.data.projects || []);
        setStudioPro(!!r.data.studio_pro);
      } catch (e) {
        toast.error("Could not load your Studio drafts");
        setProjects([]);
      }
    })();
  }, []);

  const createSession = async ({ title, withBeat }) => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post("/studio/projects", { title: title.trim() || "Untitled Session" });
      const pid = r.data.project.project_id;
      navigate(`/studio/session/${pid}${withBeat ? "?start=beat" : ""}`);
    } catch {
      toast.error("Could not start a new session");
      setCreating(false);
    }
  };

  const counts = useMemo(() => {
    const out = {};
    SESSION_TABS.forEach((t) => { out[t.key] = (projects || []).filter(t.match).length; });
    return out;
  }, [projects]);
  const visible = (projects || []).filter(SESSION_TABS.find((t) => t.key === tab).match);

  return (
    <div className="min-h-screen bg-[#08080c] text-white" data-testid="studio-page">
      <SharedNavigation activePage="studio" />
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-28" style={{ paddingTop: "calc(6rem + var(--rgm-banner-h, 0px))" }}>
        <header className="mb-6 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] tracking-widest uppercase text-amber-300/80 font-bold">RGM Creator Tools</p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="studio-heading">Studio</h1>
            <p className="text-sm text-gray-400 mt-1 max-w-xl">Record vocals over a beat, mix, master and keep your drafts.</p>
          </div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="shrink-0 h-11 pl-4 pr-3 rounded-full bg-gradient-to-b from-fuchsia-500 to-purple-700 text-white font-black text-sm tracking-wide shadow-[0_6px_20px_rgba(192,38,211,0.35)] ring-1 ring-amber-300/40 inline-flex items-center gap-1.5 active:scale-95 transition-transform"
            data-testid="studio-plus-btn"
          >
            Studio
            <span className="w-6 h-6 rounded-full bg-amber-400 text-black flex items-center justify-center text-base leading-none">+</span>
          </button>
        </header>

        <StudioProBanner studioPro={studioPro} />
        <CollabEntry />

        {!support.supported && (
          <Card className="p-4 mb-5 bg-amber-950/40 border-amber-500/40" data-testid="studio-unsupported">
            <p className="text-sm text-amber-200 font-semibold">This browser can't record audio.</p>
            <p className="text-xs text-amber-100/80 mt-1">
              Please use the latest Safari on iPhone (iOS 14.5+) or Chrome on Android to record. You can still open existing drafts.
            </p>
          </Card>
        )}

        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-300" data-testid="studio-projects-heading">My Studio Projects</h2>
          {projects && <span className="text-xs text-gray-500" data-testid="studio-session-total">{projects.length}</span>}
        </div>
        <div className="flex gap-2 mb-4 overflow-x-auto" role="tablist" data-testid="studio-session-tabs">
          {SESSION_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`shrink-0 h-8 px-3 rounded-full text-xs font-bold border transition-colors ${
                tab === t.key ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/30"
              }`}
              data-testid={`studio-tab-${t.key}`}
            >
              {t.label} <span className="opacity-60">{counts[t.key] ?? 0}</span>
            </button>
          ))}
        </div>

        {projects === null && <p className="text-sm text-gray-500" data-testid="studio-loading">Loading…</p>}
        {projects && visible.length === 0 && (
          <Card className="p-6 bg-white/[0.03] border-white/10 text-center" data-testid="studio-empty">
            <p className="text-sm text-gray-400">
              {projects.length === 0 ? "No drafts yet. Tap Studio + to start your first session." : "No sessions in this group yet."}
            </p>
          </Card>
        )}
        {visible.length > 0 && (
          <ul className="space-y-2" data-testid="studio-project-list">
            {visible.map((p) => (
              <li key={p.project_id}>
                <StudioSessionCard project={p} onOpen={() => navigate(
                  p.access_role === "collaborator" ? `/studio/session/${p.project_id}` : `/studio/project/${p.project_id}`)} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <StudioCreateSheet open={sheetOpen} creating={creating} onClose={() => setSheetOpen(false)} onCreate={createSession} />
    </div>
  );
}
